<script setup>
/**
 * FormDuplicateBtn
 * Handles record duplication by sending a copy request to the API.
 * Includes complex redirection logic for standard forms, the Site Tree,
 * and the Page Editor tool.
 */
import { ref, computed, getCurrentInstance } from 'vue';
import FormSidebarBtn from "./FormSidebarBtn.vue";

const props = defineProps({
    formId: {
        type: String,
        required: true
    }
});

const { proxy } = getCurrentInstance();
const store = proxy.$store;

// --- State ---

const isDisabled = ref(false);

// --- Computed ---

const formState = computed(() => store.state[props.formId]);

const tableName = computed(() => formState.value?.tableName);

const id = computed(() => formState.value?.id);

const show = computed(() => {
    return (
        id.value && // Don't show when creating a new item
        tableName.value !== 'account_settings' &&
        tableName.value !== 'product_options' &&
        tableName.value !== 'product_variables' &&
        tableName.value !== 'm_basket_orders' &&
        tableName.value !== 'm_registrants_registrants' &&
        tableName.value !== 'users'
    );
});

const isSiteTree = computed(() => proxy.$route.name === 'siteTree');

const isPageEditor = computed(() => proxy.$route.name === 'pageEditor');

const pageContentId = computed(() => {
    if (isPageEditor.value) {
        return proxy.$route.query.id;
    } else {
        const part = proxy.$route.path.split('/').pop() || '';
        return (part.indexOf('c') === 0) ? parseInt(part.substring(1)) : 0;
    }
});

// --- Methods ---

const onClick = async () => {
    if (!confirm('Are you sure?')) {
        return;
    }

    isDisabled.value = true;

    try {
        const { data: { id: newId } } = await store.dispatch('request/post', {
            url: `api/component/${tableName.value}?copyId=${id.value}&pageContentId=${pageContentId.value}`
        });

        if (tableName.value === 'pages' || tableName.value === 'content') {
            await store.dispatch('siteTree/init');
        }

        // Ensure category caches are refreshed so the new item appears in UI components
        await store.dispatch('categories/refreshComponentData', {
            tableName: tableName.value
        });

        if (isSiteTree.value) {
            await store.dispatch('pageContentData/init');
            isDisabled.value = false;

            if (tableName.value === 'content') {
                const obj = store.state.pageContentData.items.find(o => o.contentId === newId);
                if (obj) {
                    proxy.$router.push({
                        name: 'siteTree',
                        params: { pageOrContentId: 'c' + obj.id },
                        query: proxy.$route.query
                    });
                }
            } else {
                proxy.$router.push({
                    name: 'siteTree',
                    params: { pageOrContentId: newId },
                    query: proxy.$route.query
                });
            }
        } else if (isPageEditor.value) {
            isDisabled.value = false;

            await store.dispatch('pageContentData/init');
            const o = store.state.pageContentData.items.find(o => o.contentId === newId);
            const newPageContentId = o.id;
            const contentAreaName = o.contentArea;

            await store.dispatch('pageEditor/placeContentInPage', {
                pageContentId: newPageContentId,
                contentAreaName
            });

            await store.dispatch('pageEditor/goToContent', {
                router: proxy.$router,
                pageContentId: newPageContentId
            });
        } else {
            isDisabled.value = false;

            await proxy.$router.push({
                name: 'form',
                params: { id: newId },
                query: proxy.$route.query
            });
        }
    } catch (error) {
        isDisabled.value = false;
        console.error("Duplication failed:", error);
    }
};
</script>

<template>
    <FormSidebarBtn v-if="show" :disabled="isDisabled" @click.prevent.native="onClick">
        <i class="me-2 bi-subtract"/>
        Duplicate
    </FormSidebarBtn>
</template>